import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { LoadingService } from '../../shared/services/loading.service';
import { ToastService } from '../../shared/services/toast.service';

/** Optional: request.clone({ setHeaders: { 'X-Skip-Loading': '1' } }) */
export const SKIP_LOADING_HEADER = 'X-Skip-Loading';
/** Optional: request.clone({ setHeaders: { 'X-Skip-Error-Toast': '1' } }) */
export const SKIP_ERROR_TOAST_HEADER = 'X-Skip-Error-Toast';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {

  constructor(
    private _loading: LoadingService,
    private _toast: ToastService,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipLoading = request.headers.has(SKIP_LOADING_HEADER);
    const skipErrorToast = request.headers.has(SKIP_ERROR_TOAST_HEADER);

    let cleanReq = request;
    if (skipLoading || skipErrorToast) {
      let headers = request.headers;
      if (skipLoading) {
        headers = headers.delete(SKIP_LOADING_HEADER);
      }
      if (skipErrorToast) {
        headers = headers.delete(SKIP_ERROR_TOAST_HEADER);
      }
      cleanReq = request.clone({ headers });
    }

    if (!skipLoading) {
      this._loading.start(cleanReq.method);
    }

    return next.handle(cleanReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!skipErrorToast) {
          this.notifyHttpError(cleanReq.method, err);
        }
        return throwError(() => err);
      }),
      finalize(() => {
        if (!skipLoading) {
          this._loading.stop(cleanReq.method);
        }
      }),
    );
  }

  private notifyHttpError(method: string, err: HttpErrorResponse): void {
    // Muchos endpoints (ej. orden sin accessorials) responden 4xx por diseño.
    // Solo avisamos globalmente cuando hay fallo de red o de servidor.
    if (err.status === 0) {
      this._toast.ShowToast(
        'error',
        'No connection or the server did not respond. Check your internet and try again.',
      );
      return;
    }

    if (err.status >= 500) {
      const m = (method || 'GET').toUpperCase();
      const fallback =
        m === 'DELETE' ? 'Could not delete. Please try again.' :
        m === 'POST' || m === 'PUT' || m === 'PATCH' ? 'Could not save. Please try again.' :
        'Server error. Please try again.';

      const apiMsg =
        (typeof err.error === 'string' && err.error) ||
        err.error?.errorMessage ||
        err.error?.message;

      this._toast.ShowToast('error', apiMsg || fallback);
    }
  }
}
